import React, { useEffect, useMemo, useState } from 'react';
import { Form, Card, Button, Message, Icon } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../hooks';
import { RootState } from '../../../store';
import RecordResultMessage from '../../../components/common/recordResult';
import {
  IFranchise,
  IFranchiseEdition,
  FranchiseEditionAutoCreationType,
  FranchiseEditionType,
  FranchisePriority,
  FranchiseType,
} from '../../../models/Franchise';
import { clearFranchiseErrors, createFranchise, updateFranchise } from '../store/franchiseActions';
import { TemplateInput } from '../../../components/template/templateInput';
import CityLocationInput from '../../../components/form/cityLocationInput';
import { systemSettingsRoutes } from '../../systemSettings/constants';

interface Props {
  mode: 'create' | 'edit';
  initialValue?: IFranchise | null;
}

const defaultEdition = (): IFranchiseEdition => ({
  name: '',
  brandName: '',
  year: '',
  locationCity: '',
  locationState: '',
  locationCountry: '',
  activityType: '',
  disableComparison: false,
});

const FranchiseInput: React.FC<Props> = ({ mode, initialValue }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const franchiseState = useSelector((s: RootState) => (s as any).franchise);
  // Support both shapes: IFranchise and { data: IFranchise, ... }
  const initialRecord = React.useMemo(() => {
    const maybe = initialValue as any;
    return maybe && maybe.data ? (maybe.data as IFranchise) : (maybe as IFranchise | null) || null;
  }, [initialValue]);
  const [name, setName] = useState<string>(initialRecord?.name || '');
  const [type, setType] = useState<FranchiseType>(initialRecord?.type ?? FranchiseType.Other);
  const [priority, setPriority] = useState<FranchisePriority>(
    initialRecord?.priority ?? FranchisePriority.Low
  );
  const [editionType, setEditionType] = useState<FranchiseEditionType>(
    initialRecord?.editionType ?? FranchiseEditionType.ByYear
  );
  const [editionAutoCreation, setEditionAutoCreation] = useState<FranchiseEditionAutoCreationType>(
    initialRecord?.editionAutoCreation ?? FranchiseEditionAutoCreationType.None
  );
  const [editions, setEditions] = useState<IFranchiseEdition[]>(initialRecord?.editions || [defaultEdition()]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (initialRecord) {
      setName(initialRecord.name || '');
      setType(initialRecord.type ?? FranchiseType.Other);
      setPriority(initialRecord.priority ?? FranchisePriority.Low);
      setEditionType(initialRecord.editionType ?? FranchiseEditionType.ByYear);
      setEditionAutoCreation(initialRecord.editionAutoCreation ?? FranchiseEditionAutoCreationType.None);
      setEditions(initialRecord.editions || [defaultEdition()]);
    }
  }, [initialRecord]);

  const nonDeleted = useMemo(() => editions.filter(e => !e.isDeleted), [editions]);

  const hasUniqueEditionNames = useMemo(() => {
    const names = nonDeleted.map(e => (e.name || '').trim().toLowerCase()).filter(Boolean);
    return new Set(names).size === names.length;
  }, [nonDeleted]);

  const isValidYear = (y?: string | number | null) => {
    if (y === undefined || y === null || y === '') return true; // optional
    const str = String(y).trim();
    if (!/^\d{4}$/.test(str)) return false;
    const num = Number(str);
    return num >= 1900 && num <= 2100;
  };

  const validateEdition = (e: IFranchiseEdition) => {
    if (e.isDeleted) return true;
    // brandName is optional; year optional but must be valid if provided
    return isValidYear(e.year);
  };

  const isFormValid = () => {
    if (!hasUniqueEditionNames) return false;
    if (!nonDeleted.length) return false;
    if (!name.trim()) return false; // franchise name is required
    if (!nonDeleted.every(validateEdition)) return false;
    return true;
  };

  const onAddEdition = () => setEditions(prev => [...prev, defaultEdition()]);
  const onRemoveEdition = (idx: number) => setEditions(prev => prev.filter((_, i) => i !== idx));
  const onSoftDeleteEdition = (idx: number) => setEditions(prev => prev.map((e, i) => i === idx ? ({ ...e, isDeleted: !e.isDeleted }) : e));

  const onChangeEdition = (idx: number, patch: Partial<IFranchiseEdition>) =>
    setEditions(prev => prev.map((e, i) => i === idx ? ({ ...e, ...patch }) : e));

  const onSave = () => {
    setSubmitted(true);
    const payload: IFranchise = {
      name: name.trim(),
      type,
      priority,
      editionType,
      editionAutoCreation,
      editions: editions
        .filter(e => !e.isDeleted) // Exclude deleted editions from the API payload
        .map(e => {
          // Properly handle year: trim and only convert to number if valid
          const yearTrimmed = e.year === undefined || e.year === null ? '' : String(e.year).trim();
          const yearValue = yearTrimmed === '' ? null : Number(yearTrimmed);

          return {
            id: e.id, // include when present on edit
            name: e.name || undefined,
            brandName: e.brandName || undefined,
            year: yearValue,
            locationCity: e.locationCity || undefined,
            locationState: e.locationState || undefined,
            locationCountry: e.locationCountry || undefined,
            activityType: e.activityType || undefined,
            disableComparison: e.disableComparison || false
          };
        })
    };

    if (mode === 'create') {
      dispatch(createFranchise(payload) as any);
    } else if (mode === 'edit' && initialRecord?.id) {
      dispatch(updateFranchise(initialRecord.id, payload) as any);
    }
  };

  useEffect(() => {
    const r = mode === 'create' ? franchiseState.recordCreate : franchiseState.recordEdit;
    if (submitted && r?.ready === 0 && r?.success?.show) {
      navigate(systemSettingsRoutes.FRANCHISES);
    }
  }, [franchiseState.recordCreate, franchiseState.recordEdit, mode, navigate, submitted]);

  // Suppress success banner on initial load (e.g., after GET success) until user submits
  const recordToShow = React.useMemo(() => {
    const r = mode === 'create' ? franchiseState.recordCreate : franchiseState.recordEdit;
    if (submitted) return r;
    if (r?.success?.show) {
      return { ...r, success: { ...(r.success || {}), show: false } };
    }
    return r;
  }, [franchiseState.recordCreate, franchiseState.recordEdit, mode, submitted]);

  return (
    <div>
      <RecordResultMessage
        record={recordToShow}
        onDismiss={() => dispatch(clearFranchiseErrors(mode === 'create' ? 'recordCreate' : 'recordEdit') as any)}
      />
      {!hasUniqueEditionNames && (
        <Message negative>
          <Translate value={'franchises.page.input.validation.editionNameUnique'} />
        </Message>
      )}
      <Card fluid style={{ backgroundColor: 'var(--card-subtle-bg)' }}>
        <Card.Content>
          <Form>
            <Form.Field required>
              <label><Translate value={'franchises.page.input.name'} /></label>
              <TemplateInput
                id="franchiseName"
                path="name"
                value={name}
                onChange={(_path: string, value: any) => setName(value)}
              />
            </Form.Field>

            <Form.Group widths='equal'>
              <Form.Field required>
                <label><Translate value={'franchises.page.input.type.label'} /></label>
                <Form.Select
                  fluid
                  value={type}
                  onChange={(_e, { value }) => setType(value as FranchiseType)}
                  options={[
                    {
                      key: FranchiseType.Other,
                      value: FranchiseType.Other,
                      text: I18n.t('franchises.page.input.type.options.other.label')
                    },
                    {
                      key: FranchiseType.SingleOrganizationEvent,
                      value: FranchiseType.SingleOrganizationEvent,
                      text: I18n.t('franchises.page.input.type.options.singleOrganizationEvent.label')
                    },
                    {
                      key: FranchiseType.MultiOrganizationEvent,
                      value: FranchiseType.MultiOrganizationEvent,
                      text: I18n.t('franchises.page.input.type.options.multiOrganizationEvent.label')
                    }
                  ]}
                />
              </Form.Field>
              <Form.Field required>
                <label><Translate value={'franchises.page.input.priority.label'} /></label>
                <Form.Select
                  fluid
                  value={priority}
                  onChange={(_e, { value }) => setPriority(value as FranchisePriority)}
                  options={[
                    {
                      key: FranchisePriority.Low,
                      value: FranchisePriority.Low,
                      text: I18n.t('franchises.page.input.priority.options.low.label')
                    },
                    {
                      key: FranchisePriority.Medium,
                      value: FranchisePriority.Medium,
                      text: I18n.t('franchises.page.input.priority.options.medium.label')
                    },
                    {
                      key: FranchisePriority.High,
                      value: FranchisePriority.High,
                      text: I18n.t('franchises.page.input.priority.options.high.label')
                    },
                    {
                      key: FranchisePriority.Critical,
                      value: FranchisePriority.Critical,
                      text: I18n.t('franchises.page.input.priority.options.critical.label')
                    }
                  ]}
                />
              </Form.Field>
            </Form.Group>

            <Form.Field required>
              <label><Translate value={'franchises.page.input.editionType.label'} /></label>
              <Form.Select
                fluid
                value={editionType}
                onChange={(_e, { value }) => {
                  setEditionType(value as FranchiseEditionType);
                  setEditionAutoCreation(FranchiseEditionAutoCreationType.None);
                }}
                options={[
                  {
                    key: 'byYear',
                    value: FranchiseEditionType.ByYear,
                    text: I18n.t('franchises.page.input.editionType.options.byYear.label')
                  },
                  {
                    key: 'named',
                    value: FranchiseEditionType.Named,
                    text: I18n.t('franchises.page.input.editionType.options.named.label')
                  }
                ]}
              />
            </Form.Field>

            {editionType === FranchiseEditionType.ByYear && (
              <Form.Field>
                <label><Translate value={'franchises.page.input.editionAutoCreation.label'} /></label>
                <Message info>
                  <Translate value={'franchises.page.input.editionAutoCreation.help'} />
                </Message>
                <Form.Select
                  fluid
                  value={editionAutoCreation}
                  onChange={(_e, { value }) => setEditionAutoCreation(value as FranchiseEditionAutoCreationType)}
                  options={[
                    {
                      key: 'none',
                      value: FranchiseEditionAutoCreationType.None,
                      text: I18n.t('franchises.page.input.editionAutoCreation.options.none.label'),
                      description: I18n.t('franchises.page.input.editionAutoCreation.options.none.description')
                    },
                    {
                      key: 'sameYear',
                      value: FranchiseEditionAutoCreationType.SameYearAutoCreation,
                      text: I18n.t('franchises.page.input.editionAutoCreation.options.sameYear.label'),
                      description: I18n.t('franchises.page.input.editionAutoCreation.options.sameYear.description')
                    },
                    {
                      key: 'nextYear',
                      value: FranchiseEditionAutoCreationType.NextYearAutoCreation,
                      text: I18n.t('franchises.page.input.editionAutoCreation.options.nextYear.label'),
                      description: I18n.t('franchises.page.input.editionAutoCreation.options.nextYear.description')
                    }
                  ]}
                />
              </Form.Field>
            )}

            <div style={{ marginTop: 16 }}>
              <h3><Translate value={'franchises.page.input.editions'} /></h3>
              {editions.map((ed, idx) => (
                <Card key={idx} fluid>
                  <Card.Content>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>
                        <Translate value={'franchises.page.input.editionLabel'} /> #{idx + 1}
                        {ed.isDeleted && <span style={{ color: 'red', marginLeft: 8 }}>(<Translate value={'franchises.page.input.deleted'} />)</span>}
                      </strong>
                      <div>
                        {ed.id ? (
                          <Button type='button' color={ed.isDeleted ? 'yellow' : 'red'} onClick={() => onSoftDeleteEdition(idx)}>
                            {ed.isDeleted ? <Translate value={'franchises.page.input.restore'} /> : <Translate value={'franchises.page.input.delete'} />}
                          </Button>
                        ) : (
                          <Button type='button' onClick={() => onRemoveEdition(idx)}>
                            <Icon name='trash' />
                            <Translate value={'franchises.page.input.remove'} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <Form.Group widths='equal'>
                      <Form.Field required>
                        <label><Translate value={'franchises.page.input.edition.name'} /></label>
                        <TemplateInput
                          id={`edition-${idx}-name`}
                          path={`editions[${idx}].name`}
                          value={ed.name || ''}
                          onChange={(_path: string, value: any) => onChangeEdition(idx, { name: value })}
                        />
                      </Form.Field>
                      <Form.Field>
                        <label><Translate value={'franchises.page.input.edition.brandName'} /></label>
                        <TemplateInput
                          id={`edition-${idx}-brandName`}
                          path={`editions[${idx}].brandName`}
                          value={ed.brandName || ''}
                          onChange={(_path: string, value: any) => onChangeEdition(idx, { brandName: value })}
                        />
                      </Form.Field>
                      <Form.Field error={!!ed.year && !/^\d{4}$/.test(String(ed.year))}>
                        <label><Translate value={'franchises.page.input.edition.year'} /></label>
                        <TemplateInput
                          id={`edition-${idx}-year`}
                          path={`editions[${idx}].year`}
                          type='text'
                          value={ed.year ?? ''}
                          placeholder={'YYYY'}
                          onChange={(_path: string, value: any) => onChangeEdition(idx, { year: value })}
                        />
                      </Form.Field>
                    </Form.Group>
                    <CityLocationInput
                      idPrefix={`edition-${idx}`}
                      value={{
                        city: ed.locationCity || '',
                        state: ed.locationState || '',
                        country: ed.locationCountry || '',
                      }}
                      onChange={(next) => onChangeEdition(idx, {
                        locationCity: next.city,
                        locationState: next.state,
                        locationCountry: next.country,
                      })}
                    />
                    <Form.Group widths='equal'>
                      <Form.Field>
                        <label><Translate value={'franchises.page.input.edition.activityType'} /></label>
                        <TemplateInput
                          id={`edition-${idx}-activityType`}
                          path={`editions[${idx}].activityType`}
                          value={ed.activityType || ''}
                          onChange={(_path: string, value: any) => onChangeEdition(idx, { activityType: value })}
                        />
                      </Form.Field>
                    </Form.Group>
                    <Form.Group>
                      <Form.Field>
                        <label><Translate value={'franchises.page.input.edition.disableComparison'} /></label>
                        <Form.Select
                          fluid
                          value={ed.disableComparison ? 'true' : 'false'}
                          onChange={(_e, { value }) => onChangeEdition(idx, { disableComparison: value === 'true' })}
                          options={[
                            {
                              key: 'false',
                              value: 'false',
                              text: 'No'
                            },
                            {
                              key: 'true',
                              value: 'true',
                              text: 'Yes'
                            }
                          ]}
                        />
                      </Form.Field>
                    </Form.Group>
                  </Card.Content>
                </Card>
              ))}
              <Button type='button' onClick={onAddEdition} icon labelPosition='left'>
                <Icon name='add' /> <Translate value={'franchises.page.input.addEdition'} />
              </Button>
            </div>
          </Form>
        </Card.Content>
      </Card>
      <Button primary disabled={!isFormValid()} onClick={onSave}><Translate value={'franchises.page.input.save'} /></Button>
      <Button onClick={()=>navigate(systemSettingsRoutes.FRANCHISES)}><Translate value={'franchises.page.input.cancel'} /></Button>
    </div>
  );
};

export default FranchiseInput;
